/**
 * Tick-aligned input (Phase 3G).
 *
 * Device events carry the time they happened (DOM `timeStamp`, the same clock
 * as `performance.now()` and requestAnimationFrame). The engine runs a frame's
 * ticks back to back, each tick standing for a slice of wall-clock time; the
 * timeline applies every event to the first tick whose slice ends at or after
 * the event. A press therefore acts at the same simulated tick at any render
 * rate, instead of at the first tick of whichever frame noticed it (up to one
 * frame early in simulated time). See docs/INPUT_TIMING.md.
 *
 * The camera does not use this: mouse look stays live at render rate. Only
 * the simulation's copy of yaw/pitch follows the tick-aligned events.
 */
import type { InputFrame } from './InputFrame';

export type TimedEvent =
  | { t: number; kind: 'down'; code: string; action: string; jumpEdge: boolean }
  | { t: number; kind: 'up'; code: string }
  | { t: number; kind: 'look'; dyaw: number; dpitch: number };

/** A queue this long means ticks stopped draining it (paused, backgrounded); older events are applied at once. */
const MAX_QUEUE = 4096;

/**
 * One mouse-look delta: pitch clamped to ±limit, yaw wrapped into [−π, π] so long
 * sessions don't lose float precision (a single large delta, e.g. a pointer-lock
 * glitch, can exceed 2π). Shared by the live view and the simulation copy so the
 * two stay bit-identical.
 */
export function applyLookDelta(look: { yaw: number; pitch: number }, dyaw: number, dpitch: number, limit: number): void {
  look.yaw += dyaw;
  look.pitch = Math.max(-limit, Math.min(limit, look.pitch + dpitch));
  if (look.yaw > Math.PI || look.yaw < -Math.PI) look.yaw -= 2 * Math.PI * Math.round(look.yaw / (2 * Math.PI));
}

export class InputTimeline {
  private readonly queue: TimedEvent[] = [];
  private head = 0;
  private readonly held = new Map<string, string>();
  private jumpEdges = 0;
  private readonly look = { yaw: 0, pitch: 0 };
  /** Latest accepted timestamp: events are kept in time order (see push). */
  private lastT = -Infinity;

  constructor(private readonly pitchLimit: number) {}

  /**
   * Queues an event. A timestamp earlier than the previous event's (keyboard and
   * mouse stamps can come from different OS sources) is raised to it, so one
   * out-of-order stamp cannot hold back the events behind it.
   */
  push(e: TimedEvent): void {
    const t = e.t < this.lastT ? this.lastT : e.t;
    this.lastT = t;
    this.queue.push(t === e.t ? e : { ...e, t });
    if (this.queue.length - this.head > MAX_QUEUE) this.apply(this.queue[this.head++]!);
    // Compact consumed entries when nothing drains the queue (paused, replaying).
    if (this.head >= MAX_QUEUE) {
      this.queue.splice(0, this.head);
      this.head = 0;
    }
  }

  /** Applies, in arrival order, every queued event that happened at or before `tMs`. */
  drainTo(tMs: number): void {
    while (this.head < this.queue.length && this.queue[this.head]!.t <= tMs) this.apply(this.queue[this.head++]!);
    if (this.head === this.queue.length) {
      this.queue.length = 0;
      this.head = 0;
    }
  }

  /** Fills `out` from the applied state and consumes jump edges. `active` gates movement keys. */
  fill(out: InputFrame, active: boolean): InputFrame {
    let fwd = 0;
    let back = 0;
    let left = 0;
    let right = 0;
    let jump = 0;
    let crouch = 0;
    if (active) {
      for (const action of this.held.values()) {
        if (action === 'forward') fwd = 1;
        else if (action === 'back') back = 1;
        else if (action === 'left') left = 1;
        else if (action === 'right') right = 1;
        else if (action === 'jump') jump = 1;
        else if (action === 'crouch') crouch = 1;
      }
    }
    out.moveX = right - left;
    out.moveZ = fwd - back;
    out.yaw = this.look.yaw;
    out.pitch = this.look.pitch;
    out.jumpHeld = jump === 1;
    out.crouchHeld = crouch === 1;
    out.jumpPressed = active && this.jumpEdges > 0;
    this.jumpEdges = 0;
    return out;
  }

  /** Sets the simulation's look angles and drops pending look deltas (teleport, replay, setYaw). */
  setLook(yaw: number, pitch: number): void {
    this.look.yaw = yaw;
    this.look.pitch = pitch;
    let w = this.head;
    for (let r = this.head; r < this.queue.length; r++) if (this.queue[r]!.kind !== 'look') this.queue[w++] = this.queue[r]!;
    this.queue.length = w;
  }

  /** Forgets everything: held keys, pending events and edges. */
  reset(yaw: number, pitch: number): void {
    this.queue.length = 0;
    this.head = 0;
    this.held.clear();
    this.jumpEdges = 0;
    this.lastT = -Infinity;
    this.look.yaw = yaw;
    this.look.pitch = pitch;
  }

  /** Applies everything queued and drops jump edges: input that arrived while a replay drove the ticks. */
  discardPending(): void {
    this.drainTo(Infinity);
    this.jumpEdges = 0;
  }

  get pending(): number {
    return this.queue.length - this.head;
  }

  private apply(e: TimedEvent): void {
    if (e.kind === 'down') {
      this.held.set(e.code, e.action);
      if (e.jumpEdge) this.jumpEdges++;
    } else if (e.kind === 'up') {
      this.held.delete(e.code);
    } else {
      applyLookDelta(this.look, e.dyaw, e.dpitch, this.pitchLimit);
    }
  }
}
